import { useState } from 'react'
import { Link } from 'react-router-dom'

const PLANET_SYMBOLS = ['☉', '☽', '♂', '♃', '♄', '♅', '♆', '☿', '♀']

function StarField() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {/* Nebula gradients */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 80% 60% at 50% -10%, rgba(107,33,168,0.25) 0%, transparent 60%),
            radial-gradient(ellipse 50% 50% at 90% 90%, rgba(59,7,100,0.2) 0%, transparent 60%),
            radial-gradient(ellipse 40% 40% at 10% 70%, rgba(36,36,112,0.2) 0%, transparent 60%)
          `,
        }}
      />
      {/* Floating planet glyphs */}
      {PLANET_SYMBOLS.map((sym, i) => (
        <span
          key={i}
          className="absolute text-star-dim/10 font-mono select-none"
          style={{
            left: `${8 + i * 10}%`,
            top: `${10 + (i % 4) * 20}%`,
            fontSize: `${16 + (i % 3) * 8}px`,
            animation: `twinkle ${3 + i * 0.4}s ease-in-out infinite`,
            animationDelay: `${i * 0.5}s`,
          }}
        >
          {sym}
        </span>
      ))}
    </div>
  )
}

function NavBar() {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-cosmic-mist/50 bg-cosmic-void/80 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group" onClick={() => setMobileOpen(false)}>
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-deep to-violet-mid flex items-center justify-center shadow-glow-violet">
            <span className="text-gold text-sm">✦</span>
          </div>
          <span className="font-display text-xl font-light text-star-white tracking-wide group-hover:text-gold transition-colors">
            Shubh Time
          </span>
        </Link>

        {/* Desktop nav links */}
        <nav className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-sm text-star-soft hover:text-star-bright transition-colors">Features</a>
          <Link to="/pricing" className="text-sm text-star-soft hover:text-star-bright transition-colors">Pricing</Link>
          <a href="/api-docs" className="text-sm text-star-soft hover:text-star-bright transition-colors">API</a>
          <Link
            to="/login"
            className="text-sm px-4 py-1.5 rounded-lg border border-cosmic-mist text-star-soft hover:border-violet-dark hover:text-star-bright transition-all"
          >
            Sign in
          </Link>
          <Link
            to="/login"
            className="text-sm px-4 py-1.5 rounded-lg bg-gradient-to-r from-violet to-violet-mid text-star-white font-medium hover:shadow-glow-violet transition-all"
          >
            Get started free
          </Link>
        </nav>

        {/* Mobile: hamburger + CTA */}
        <div className="md:hidden flex items-center gap-3">
          <Link
            to="/login"
            className="text-sm px-4 py-1.5 rounded-lg bg-gradient-to-r from-violet to-violet-mid text-star-white font-medium"
          >
            Get started
          </Link>
          <button
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            className="w-9 h-9 flex flex-col items-center justify-center gap-1.5 rounded-lg border border-cosmic-mist text-star-soft hover:border-violet-dark transition-colors"
          >
            <span
              className={`block w-4 h-px bg-current transition-transform duration-200 ${mobileOpen ? 'translate-y-[5px] rotate-45' : ''}`}
            />
            <span
              className={`block w-4 h-px bg-current transition-opacity duration-200 ${mobileOpen ? 'opacity-0' : ''}`}
            />
            <span
              className={`block w-4 h-px bg-current transition-transform duration-200 ${mobileOpen ? '-translate-y-[5px] -rotate-45' : ''}`}
            />
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <nav className="md:hidden border-t border-cosmic-mist/30 bg-cosmic-void/95 backdrop-blur-md px-6 py-4 flex flex-col gap-1">
          <a
            href="#features"
            onClick={() => setMobileOpen(false)}
            className="py-3 text-sm text-star-soft hover:text-star-bright border-b border-cosmic-mist/20 transition-colors"
          >
            Features
          </a>
          <Link
            to="/pricing"
            onClick={() => setMobileOpen(false)}
            className="py-3 text-sm text-star-soft hover:text-star-bright border-b border-cosmic-mist/20 transition-colors"
          >
            Pricing
          </Link>
          <a
            href="/api-docs"
            onClick={() => setMobileOpen(false)}
            className="py-3 text-sm text-star-soft hover:text-star-bright border-b border-cosmic-mist/20 transition-colors"
          >
            API
          </a>
          <Link
            to="/login"
            onClick={() => setMobileOpen(false)}
            className="mt-3 py-2.5 text-center text-sm rounded-lg border border-cosmic-mist text-star-soft hover:border-violet-dark transition-all"
          >
            Sign in
          </Link>
        </nav>
      )}
    </header>
  )
}

function HeroSection() {
  return (
    <section className="relative pt-32 pb-24 px-6 text-center">
      <div className="max-w-4xl mx-auto">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-gold/20 bg-gold/5 text-gold text-xs font-mono mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
          Muhurta as a Service • Now in public beta
        </div>

        {/* Headline */}
        <h1 className="font-display text-5xl md:text-7xl font-light text-star-white leading-tight mb-6">
          Auspicious timing,{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-gold-bright">
            precisely calculated
          </span>
        </h1>

        <p className="text-star-soft text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10">
          Shubh Time applies 5,000 years of Vedic astrology — Panchanga, Muhurta, Dasha — through
          a dual-validated engine. Know the best moment for every decision.
        </p>

        {/* CTA row */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          <Link
            to="/login"
            className="px-8 py-3 rounded-xl bg-gradient-to-r from-violet to-violet-mid text-star-white font-medium text-base
                       hover:shadow-glow-violet transition-all active:scale-[0.98]"
          >
            Start Free Trial
          </Link>
          <a
            href="/api-docs"
            className="px-8 py-3 rounded-xl border border-cosmic-mist text-star-soft text-base
                       hover:border-violet-dark hover:text-star-bright transition-all"
          >
            View API docs →
          </a>
        </div>

        {/* Social proof mini */}
        <p className="text-star-dim text-sm font-mono">
          Dual-engine validation • Swiss Ephemeris + AstrologyAPI • Used by astrologers worldwide
        </p>
      </div>
    </section>
  )
}

const FEATURES = [
  {
    icon: '☉',
    title: 'Birth Chart Engine',
    description: 'Complete Jyotish chart with all divisional charts (D1–D60), Ashtakavarga, and Shadbala. Dual-engine validated for maximum accuracy.',
    tag: 'Core',
  },
  {
    icon: '⏱',
    title: 'Muhurta Calculator',
    description: 'Find auspicious windows for any event using the 10-step Panchanga pipeline: Tithi, Vara, Nakshatra, Yoga, Karana, and more.',
    tag: 'Muhurta',
  },
  {
    icon: '🌊',
    title: 'Dasha Timeline',
    description: 'Complete Vimshottari Dasha sequence with sub-periods (Antardasha, Pratyantardasha) and interpretations for life event timing.',
    tag: 'Dasha',
  },
  {
    icon: '🕐',
    title: 'Daily Choghadiya',
    description: 'Hour-by-hour Choghadiya timings with quality ratings. Know which hours are auspicious, neutral, or inauspicious every day.',
    tag: 'Daily',
  },
  {
    icon: '♂',
    title: 'Compatibility Analysis',
    description: 'Ashtakoot Milan scoring (36-point system) with Guna matching, Mangal Dosha analysis, and detailed compatibility report.',
    tag: 'Matching',
  },
  {
    icon: '✦',
    title: 'Developer API',
    description: 'Full REST API access to all calculations. Build apps, automate pipelines, or integrate Vedic astrology into any platform.',
    tag: 'API',
  },
]

function FeaturesSection() {
  return (
    <section id="features" className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-gold font-mono text-sm mb-3 tracking-widest">CAPABILITIES</p>
          <h2 className="font-display text-4xl font-light text-star-white mb-4">
            Complete Vedic astrology stack
          </h2>
          <p className="text-star-soft max-w-xl mx-auto">
            Every calculation runs through Swiss Ephemeris — the gold standard in astronomical precision — cross-validated with a secondary engine.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className="card-cosmic p-6 group">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-violet-deep/50 border border-violet/20 flex items-center justify-center text-lg shrink-0 group-hover:border-violet/40 transition-colors">
                  {f.icon}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <h3 className="text-star-bright font-medium text-sm">{f.title}</h3>
                    <span className="text-2xs text-gold/60 font-mono bg-gold/5 border border-gold/10 px-1.5 py-0.5 rounded">
                      {f.tag}
                    </span>
                  </div>
                  <p className="text-star-soft text-sm leading-relaxed">{f.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

const PRICING_TIERS_COMPACT = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Start exploring Vedic astrology',
    cta: 'Get started',
    ctaVariant: 'outline' as const,
    features: [
      'Birth chart (natal)',
      'Basic Dasha view',
      '3 Muhurta lookups / month',
      'Daily Choghadiya',
    ],
  },
  {
    name: 'Premium',
    price: '$9.99',
    period: '/month',
    description: 'Full access for serious practitioners',
    cta: 'Start Free Trial',
    ctaVariant: 'primary' as const,
    highlight: true,
    badge: 'Most popular',
    features: [
      'Everything in Free',
      'Unlimited Muhurta lookups',
      'Full Dasha interpretation',
      'Compatibility analysis',
      'Calendar export (.ics)',
    ],
  },
  {
    name: 'Annual',
    price: '$89.99',
    period: '/year',
    description: 'Premium at ~25% off',
    cta: 'Start Annual',
    ctaVariant: 'outline' as const,
    badge: 'Save 25%',
    features: [
      'Everything in Premium',
      'Priority email support',
      'Early access to new features',
    ],
  },
]

function PricingCardCompact({ tier }: { tier: (typeof PRICING_TIERS_COMPACT)[number] }) {
  return (
    <div
      className={`relative card-cosmic p-6 flex flex-col gap-4 ${
        tier.highlight
          ? 'border-violet/60 shadow-glow-violet ring-1 ring-violet/20'
          : ''
      }`}
    >
      {tier.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-3 py-1 rounded-full text-2xs font-mono bg-gradient-to-r from-violet to-violet-mid text-star-white shadow-glow-violet">
            {tier.badge}
          </span>
        </div>
      )}

      <div>
        <h3 className="text-star-bright font-semibold mb-1">{tier.name}</h3>
        <p className="text-star-dim text-xs">{tier.description}</p>
      </div>

      <div className="flex items-end gap-1">
        <span className="font-display text-3xl font-light text-star-white">{tier.price}</span>
        <span className="text-star-muted text-xs mb-1">{tier.period}</span>
      </div>

      <Link
        to="/login"
        className={`w-full text-center py-2 rounded-lg text-sm font-medium transition-all ${
          tier.ctaVariant === 'primary'
            ? 'bg-gradient-to-r from-violet to-violet-mid text-star-white hover:shadow-glow-violet'
            : 'border border-cosmic-mist text-star-soft hover:border-violet-dark hover:text-star-bright'
        }`}
      >
        {tier.cta}
      </Link>

      <ul className="space-y-2">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-xs text-star-soft">
            <span className="text-auspicious mt-0.5 shrink-0">✓</span>
            {f}
          </li>
        ))}
      </ul>
    </div>
  )
}

function PricingPreviewSection() {
  return (
    <section id="pricing" className="py-24 px-6 border-t border-cosmic-mist/30">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-gold font-mono text-sm mb-3 tracking-widest">PRICING</p>
          <h2 className="font-display text-4xl font-light text-star-white mb-4">
            Start free, scale when ready
          </h2>
          <p className="text-star-soft max-w-xl mx-auto">
            No credit card required. Upgrade or cancel any time.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {PRICING_TIERS_COMPACT.map((tier) => (
            <PricingCardCompact key={tier.name} tier={tier} />
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1.5 text-sm text-gold hover:text-gold-bright transition-colors"
          >
            View full pricing &amp; API plans →
          </Link>
        </div>
      </div>
    </section>
  )
}

const TESTIMONIALS = [
  {
    quote: "The dual-engine validation gives me confidence I can't get from any other tool. Every Muhurta I calculate now gets cross-checked automatically.",
    author: 'Priya M.',
    role: 'Vedic astrologer, Mumbai',
    initials: 'PM',
  },
  {
    quote: "I integrated the API into my calendar app in an afternoon. The documentation is clear, the responses are fast, and the Dasha data is spot-on.",
    author: 'Arjun S.',
    role: 'Full-stack developer',
    initials: 'AS',
  },
  {
    quote: "Finally a modern astrology platform that takes accuracy seriously. The Panchanga pipeline handles edge cases that other apps simply get wrong.",
    author: 'Kavitha R.',
    role: 'Jyotish practitioner',
    initials: 'KR',
  },
]

function TestimonialsSection() {
  return (
    <section className="py-24 px-6 border-t border-cosmic-mist/30">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-gold font-mono text-sm mb-3 tracking-widest">TESTIMONIALS</p>
          <h2 className="font-display text-4xl font-light text-star-white mb-4">
            Trusted by practitioners &amp; builders
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={t.author} className="card-cosmic p-6 flex flex-col gap-4">
              {/* Stars */}
              <div className="flex gap-1" aria-label="5 stars">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-gold text-sm">★</span>
                ))}
              </div>
              <p className="text-star-soft text-sm leading-relaxed flex-1">"{t.quote}"</p>
              <div className="flex items-center gap-3 pt-2 border-t border-cosmic-mist/30">
                <div className="w-8 h-8 rounded-full bg-violet-deep/60 border border-violet/20 flex items-center justify-center shrink-0">
                  <span className="text-violet-glow font-mono text-xs">{t.initials}</span>
                </div>
                <div>
                  <p className="text-star-bright text-xs font-medium">{t.author}</p>
                  <p className="text-star-dim text-2xs">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

const TRUST_SIGNALS = [
  { icon: '🛡', label: 'Swiss Ephemeris', sub: 'Gold standard astronomical precision' },
  { icon: '⚡', label: 'Dual-engine validation', sub: 'Cross-checked against 2 independent sources' },
  { icon: '🔒', label: 'Privacy first', sub: 'Your chart data is never sold or shared' },
  { icon: '🌐', label: 'Global timezone', sub: 'Accurate for any location on Earth' },
]

function TrustSection() {
  return (
    <section className="py-24 px-6 border-t border-cosmic-mist/30">
      <div className="max-w-6xl mx-auto">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {TRUST_SIGNALS.map((t) => (
            <div key={t.label} className="text-center p-6">
              <div className="text-3xl mb-3">{t.icon}</div>
              <h4 className="text-star-bright font-medium text-sm mb-1">{t.label}</h4>
              <p className="text-star-dim text-xs leading-relaxed">{t.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function CtaSection() {
  return (
    <section className="py-24 px-6 border-t border-cosmic-mist/30">
      <div className="max-w-2xl mx-auto text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-deep to-violet-mid mb-6 shadow-glow-violet">
          <span className="text-gold text-xl">✦</span>
        </div>
        <h2 className="font-display text-4xl font-light text-star-white mb-4">
          Start your free 7-day trial
        </h2>
        <p className="text-star-soft mb-8">
          No credit card required. Full Premium access for 7 days, free forever after.
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-violet to-violet-mid text-star-white font-medium text-base
                     hover:shadow-glow-violet transition-all active:scale-[0.98]"
        >
          Start Free Trial
        </Link>
        <p className="text-star-dim text-xs mt-6 font-display italic">
          "कालो हि परमो धर्मः" — Time is the supreme dharma
        </p>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="border-t border-cosmic-mist/30 py-12 px-6">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-deep to-violet-mid flex items-center justify-center">
            <span className="text-gold text-xs">✦</span>
          </div>
          <span className="font-display text-lg text-star-soft">Shubh Time</span>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/pricing" className="text-xs text-star-dim hover:text-star-soft transition-colors">Pricing</Link>
          <Link to="/login" className="text-xs text-star-dim hover:text-star-soft transition-colors">Sign in</Link>
          <a href="/api-docs" className="text-xs text-star-dim hover:text-star-soft transition-colors">API Docs</a>
          <a href="/terms" className="text-xs text-star-dim hover:text-star-soft transition-colors">Terms</a>
          <a href="/privacy" className="text-xs text-star-dim hover:text-star-soft transition-colors">Privacy</a>
          <a href="mailto:hello@shubhtime.app" className="text-xs text-star-dim hover:text-star-soft transition-colors">Contact</a>
        </div>
        <p className="text-xs text-star-dim font-mono">
          © 2026 Shubh Time · शुभ समय
        </p>
      </div>
    </footer>
  )
}

export function LandingPage() {
  return (
    <div className="min-h-screen bg-cosmic-void relative overflow-x-hidden">
      <StarField />
      <div className="relative z-10">
        <NavBar />
        <main>
          <HeroSection />
          <FeaturesSection />
          <TestimonialsSection />
          <PricingPreviewSection />
          <TrustSection />
          <CtaSection />
        </main>
        <Footer />
      </div>
    </div>
  )
}
